import { useEffect } from 'react';
import {
  useDispatch,
  useSelector,
  type TypedUseSelectorHook,
} from 'react-redux';
import {
  Button,
  ComboBox,
  Input,
  Label,
  ListBox,
  ListBoxItem,
  Popover,
} from 'react-aria-components';
import cx from 'classnames';
import { defineMessages, useIntl } from 'react-intl';

import config from '@plone/volto/registry';
import { searchContent } from '@plone/volto/actions/search/search';

import ContactFormHeader from '../Common/ContactFormHeader/ContactFormHeader';
import type { QubicaViewProps, RequestItem } from '../Common/utils';
import ChevronDownSVG from '../../Icons/ChevronDownSVG';

import styles from './styles.module.css';
import QubicaBaseFolderMap from './Component/QubicaBaseFolderMap';
import { getQBasesNationList } from '@redturtle/qubicaamf-theme/actions';
import type { QBasesNationListInitialState } from '@redturtle/qubicaamf-theme/reducers';

export interface RootState {
  search: Request & {
    subrequests: {
      qubicaBases: RequestItem;
    };
  };
  qBasesNationList: QBasesNationListInitialState;
}

const QubicaBaseFolderView = (props: QubicaViewProps) => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const useTypedSelector: TypedUseSelectorHook<RootState> =
    useSelector<RootState>;

  const Container = config.getComponent('Container').component || 'div';

  useEffect(() => {
    dispatch(getQBasesNationList());
  }, [dispatch]);

  useEffect(() => {
    dispatch(
      searchContent(
        '/',
        { portal_type: ['QubicaBase'], b_size: 1000 },
        'qubicaBases',
      ),
    );
  }, [dispatch]);

  const nations = useTypedSelector((state) => state.qBasesNationList);

  const qubicaBases = useTypedSelector(
    (state) => state.search.subrequests.qubicaBases,
  );

  const qubicaBasesCoordinates = qubicaBases
    ? qubicaBases.items.map((qb) => {
        return {
          name: qb.title,
          lat: Number(qb.latitude),
          lng: Number(qb.longitude),
          link: qb['@id'],
          base_type: qb.base_type,
        };
      })
    : [];

  return (
    <Container>
      <ContactFormHeader content={props.content} />

      <div className={styles.countrySelectorContainer}>
        <div className={styles.selectContainer}>
          <ComboBox
            // placeholder={intl.formatMessage(messages.select_nation_placeholder)}
            // isOpen={nationMenuIsOpen}
            className={styles.select}
            name="prova"
          >
            <Label className={styles.selectLabel}>
              {intl.formatMessage(messages.select_nation_label)}
            </Label>
            <div className={styles.nationsCombobox}>
              <Input
                placeholder={intl.formatMessage(
                  messages.select_nation_placeholder,
                )}
                className={cx('react-aria-Input', styles.nationsComboboxInput)}
              />
              <Button
                className={cx('react-aria-Button', styles.selectButton)}
                excludeFromTabOrder={false}
              >
                <ChevronDownSVG />
              </Button>
            </div>

            <Popover
              isNonModal={true}
              className={cx(styles.popover, 'react-aria-Popover')}
              trigger="prova"
            >
              <ListBox className={cx('react-aria-ListBox', styles.listbox)}>
                {nations.data.map((nation) => (
                  <ListBoxItem
                    key={nation.id}
                    id={nation.id}
                    textValue={nation.name}
                    href={nation.loc_url}
                    className={cx('react-aria-ListBoxItem', styles.option)}
                  >
                    {nation.name}
                  </ListBoxItem>
                ))}
              </ListBox>
            </Popover>
          </ComboBox>
        </div>
      </div>

      <div className={styles.mapSection}>
        <div className={styles.mapSectionTitle}>
          <h2>{intl.formatMessage(messages.map_title)}</h2>
        </div>
        <div className={styles.mapContainer}>
          <QubicaBaseFolderMap
            qubicaBases={qubicaBasesCoordinates}
            basesLoading={qubicaBases?.loading}
          />
        </div>
      </div>
    </Container>
  );
};

export default QubicaBaseFolderView;

const messages = defineMessages({
  select_nation_label: {
    id: 'select_nation_label',
    defaultMessage: 'Select your state/country from the following list',
  },
  select_nation_placeholder: {
    id: 'select_nation_placeholder',
    defaultMessage: 'Select a State / Country',
  },
  map_title: {
    id: 'map_title',
    defaultMessage: 'QubicaAMF around the World',
  },
});
